import {
	ReactNode,
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";

interface PricingOptions {
	existingWarrantyRate: number;
	isNonPerformingContractor: boolean;
	brokerFees: Record<number, number>; // warranty ID -> broker fees
	cnrContractorStates: Record<number, boolean>; // warranty ID -> CNR contractor state
	pjSelectedStates: Record<number, boolean>; // warranty ID -> PJ selected state
	existingDamagesRates: Record<number, number>; // warranty ID -> existing damages rate
	existingDamagesChecked: Record<number, boolean>; // warranty ID -> existing damages checkbox state
}

interface PricingOptionsContextType {
	pricingOptions: PricingOptions;
	updateBrokerFees: (warrantyId: number, fees: number) => void;
	updateCnrContractorState: (
		warrantyId: number,
		isNonPerforming: boolean,
	) => void;
	updatePjSelectedState: (warrantyId: number, isSelected: boolean) => void;
	updateExistingDamagesRate: (warrantyId: number, rate: number) => void;
	updateExistingDamagesChecked: (
		warrantyId: number,
		isChecked: boolean,
	) => void;
	getPricingOptionsForWarranty: (warrantyId: number) => {
		existingWarrantyRate: number;
		isNonPerformingContractor: boolean;
		isPjSelected: boolean;
		brokerFees: number;
	};
}

const PricingOptionsContext = createContext<
	PricingOptionsContextType | undefined
>(undefined);

export const PricingOptionsProvider = ({
	children,
}: { children: ReactNode }) => {
	const [pricingOptions, setPricingOptions] = useState<PricingOptions>({
		existingWarrantyRate: 0.1,
		isNonPerformingContractor: false,
		brokerFees: {},
		cnrContractorStates: {},
		pjSelectedStates: {},
		existingDamagesRates: {},
		existingDamagesChecked: {},
	});

	const updateBrokerFeesMemoized = useCallback(
		(warrantyId: number, fees: number) => {
			setPricingOptions((prev) => ({
				...prev,
				brokerFees: {
					...prev.brokerFees,
					[warrantyId]: fees,
				},
			}));
		},
		[],
	);

	const updateCnrContractorStateMemoized = useCallback(
		(warrantyId: number, isNonPerforming: boolean) => {
			setPricingOptions((prev) => ({
				...prev,
				cnrContractorStates: {
					...prev.cnrContractorStates,
					[warrantyId]: isNonPerforming,
				},
			}));
		},
		[],
	);

	const updatePjSelectedStateMemoized = useCallback(
		(warrantyId: number, isSelected: boolean) => {
			setPricingOptions((prev) => ({
				...prev,
				pjSelectedStates: {
					...prev.pjSelectedStates,
					[warrantyId]: isSelected,
				},
			}));
		},
		[],
	);

	const updateExistingDamagesRateMemoized = useCallback(
		(warrantyId: number, percentageValue: number) => {
			// Convert percentage value (10, 20, 30) to decimal (0.1, 0.2, 0.3)
			const rate = percentageValue / 100;
			setPricingOptions((prev) => ({
				...prev,
				existingDamagesRates: {
					...prev.existingDamagesRates,
					[warrantyId]: rate,
				},
			}));
		},
		[],
	);

	const updateExistingDamagesCheckedMemoized = useCallback(
		(warrantyId: number, isChecked: boolean) => {
			setPricingOptions((prev) => ({
				...prev,
				existingDamagesChecked: {
					...prev.existingDamagesChecked,
					[warrantyId]: isChecked,
				},
			}));
		},
		[],
	);

	const getPricingOptionsForWarranty = useCallback(
		(warrantyId: number) => {
			// Check if existing damages checkbox is checked for any warranty
			const hasCheckedExistingDamages = Object.values(
				pricingOptions.existingDamagesChecked,
			).some((checked) => checked);

			// Get existing damages rate only if checkbox is checked
			let existingWarrantyRate = 0; // Default to 0 if not checked
			if (hasCheckedExistingDamages) {
				// If checkbox is checked, use the select value if available, otherwise 0
				const hasSpecificExistingDamagesRate =
					Object.keys(pricingOptions.existingDamagesRates).length > 0;
				existingWarrantyRate = hasSpecificExistingDamagesRate
					? Object.values(pricingOptions.existingDamagesRates)[0] // Use the selected rate from select
					: 0; // Use 0 if checkbox checked but no select value
			}

		const hasSpecificCnrState =
			Object.keys(pricingOptions.cnrContractorStates).length > 0;
		const specificCnrState = hasSpecificCnrState
			? Object.values(pricingOptions.cnrContractorStates)[0]
			: pricingOptions.isNonPerformingContractor;

		const isPjSelected = pricingOptions.pjSelectedStates[warrantyId] ?? false;

		return {
			existingWarrantyRate,
			isNonPerformingContractor: specificCnrState,
			isPjSelected,
			brokerFees: pricingOptions.brokerFees[warrantyId] || 0,
		};
		},
		[pricingOptions],
	);
	const contextValue = useMemo(
		() => ({
			pricingOptions,
			updateBrokerFees: updateBrokerFeesMemoized,
			updateCnrContractorState: updateCnrContractorStateMemoized,
			updatePjSelectedState: updatePjSelectedStateMemoized,
			updateExistingDamagesRate: updateExistingDamagesRateMemoized,
			updateExistingDamagesChecked: updateExistingDamagesCheckedMemoized,
			getPricingOptionsForWarranty,
		}),
		[
			pricingOptions,
			updateBrokerFeesMemoized,
			updateCnrContractorStateMemoized,
			updatePjSelectedStateMemoized,
			updateExistingDamagesRateMemoized,
			updateExistingDamagesCheckedMemoized,
			getPricingOptionsForWarranty,
		],
	);

	return (
		<PricingOptionsContext.Provider value={contextValue}>
			{children}
		</PricingOptionsContext.Provider>
	);
};

export const usePricingOptions = () => {
	const context = useContext(PricingOptionsContext);
	if (!context) {
		throw new Error(
			"usePricingOptions must be used within a PricingOptionsProvider",
		);
	}
	return context;
};
